import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "wrapper", "content", "countdown", "closeButton", "countdownNumber"]
  static values = {
    url: String,
    isPartner: Boolean,
    bookingId: String,
    userId: String,
    countdown: Number
  }

  connect() {
    this.countdownValue = 15
  }

  showDetails(event) {
    event.preventDefault()

    const url = event.currentTarget.dataset.bookingDetailsUrlValue
    this.urlValue = url
    this.bookingIdValue = event.currentTarget.dataset.bookingId
    this.userIdValue = event.currentTarget.dataset.userId

    // Empêcher le défilement de l'arrière-plan
    document.body.classList.add('overflow-hidden')

    // Afficher la modal avec animation
    this.modalTarget.classList.remove('hidden')
    setTimeout(() => {
      this.wrapperTarget.classList.remove('scale-95', 'opacity-0')
      this.wrapperTarget.classList.add('scale-100', 'opacity-100')
    }, 10)

    // Mettre à jour le statut
    this.updateBookingStatus()

    if (this.isPartnerValue) {
      this.loadBookingDetails()
    } else {
      this.showPartnershipPrompt()
      this.startCountdown()
      setTimeout(() => {
        this.loadBookingDetails()
      }, 15000)
    }
  }

  startCountdown() {
    this.closeButtonTarget.style.visibility = 'hidden'
    this.countdownTarget.classList.remove('hidden')
    this.countdownNumberTarget.textContent = this.countdownValue

    const countdownInterval = setInterval(() => {
      if (this.countdownValue > 0) {
        this.countdownValue--
        this.countdownNumberTarget.textContent = this.countdownValue
      } else {
        clearInterval(countdownInterval)
        this.hideCountdown()
      }
    }, 1000)

    this.modalTarget.addEventListener('close', () => {
      clearInterval(countdownInterval)
      this.countdownValue = 15
      this.hideCountdown()
    }, { once: true })
  }

  hideCountdown() {
    this.countdownTarget.classList.add('hidden')
    this.closeButtonTarget.style.visibility = 'visible'
  }

  showPartnershipPrompt() {
    this.contentTarget.innerHTML = `
      <div class="p-4 text-center bg-blue-50 rounded-lg">
        <p class="mb-2 italic text-xs animate-pulse">Chargement en cours...les détails seront affichés automatiquement</p>
        <h2 class="text-xl font-bold mb-2">Booste ton activité !</h2>
        <p class="mb-2">En tant que partenaire, tu profites d'avantages <span class="font-bold">rien que pour toi</span> :</p>
        <ul class="mb-3 text-left space-y-2">
          <li class="flex items-start">
            <span class="mr-2 mt-1">📧</span>
            <div>
              <span class="font-medium">Gagne du temps</span><br>
              <span class="text-sm">Reçois tes demandes de réservation <span class="font-bold">direct</span> dans ta boîte mail.</span>
            </div>
          </li>
          <li class="flex items-start">
            <span class="mr-2 mt-1">⭐</span>
            <div>
              <span class="font-medium">Mets ton hébergement en avant</span><br>
              <span class="text-sm">On t'aide à te <span class="font-bold">démarquer</span> et à attirer <span class="font-bold">plus de monde</span>.</span>
            </div>
          </li>
          <li class="flex items-start">
            <span class="mr-2 mt-1">⚡</span>
            <div>
              <span class="font-medium">Support ultra rapide</span><br>
              <span class="text-sm">On te répond en <span class="font-bold">24/48h</span>, pas de temps perdu !</span>
            </div>
          </li>
          <li class="flex items-start">
            <span class="mr-2 mt-1">🏠</span>
            <div>
              <span class="font-medium">On t'accompagne</span><br>
              <span class="text-sm">Un <span class="font-bold">vrai coup de main</span> pour faire tourner ton hébergement <span class="font-bold">à fond</span>.</span>
            </div>
          </li>
        </ul>
        <div class="flex flex-col justify-center items-center space-y-2">
          <a href="/adhesion/professionnels" 
             class="bg-blue text-yellow hover:bg-yellow hover:text-blue px-6 py-2 rounded-lg font-medium">
            Découvrir les offres partenaires
          </a>
          <a href="/adhesion/professionnels#pricing" 
             class="text-blue hover:text-blue-600 underline text-sm">
            Souscrire maintenant
          </a>
        </div>
      </div>
    `
  }

  async updateBookingStatus() {
    try {
      const response = await fetch(`/reservations/${this.bookingIdValue}/mark_as_seen`, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
          'Accept': 'application/json'
        },
        credentials: 'same-origin'
      })

      if (response.ok) {
        const statusBadges = document.querySelectorAll(`[data-booking-id="${this.bookingIdValue}"] .status-badge`)
        statusBadges.forEach(badge => {
          badge.classList.remove('bg-yellow-100', 'text-yellow-800')
          badge.classList.add('bg-green-100', 'text-green-800')
          badge.innerHTML = '✅ Traitée'
        })
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour du statut:", error)
    }
  }

  async loadBookingDetails() {
    try {
      const response = await fetch(this.urlValue)
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const html = await response.text()
      this.contentTarget.innerHTML = html
      this.hideCountdown()
    } catch (error) {
      console.error("Erreur lors du chargement des détails:", error)
      this.contentTarget.innerHTML = `
        <div class="p-4 text-red-500">
          Une erreur est survenue lors du chargement des détails.
        </div>
      `
      this.hideCountdown()
    }
  }


  closeDialog(event) {
    event.preventDefault()

    // Réactiver le défilement de l'arrière-plan
    document.body.classList.remove('overflow-hidden')

    // Animation de fermeture
    this.wrapperTarget.classList.add('scale-95', 'opacity-0')
    this.wrapperTarget.classList.remove('scale-100', 'opacity-100')

    setTimeout(() => {
      this.modalTarget.classList.add('hidden')
      this.contentTarget.innerHTML = "" // Nettoyer le contenu
      this.countdownValue = 15 // Réinitialiser le compte à rebours
    }, 300)
  }

  // Déconnexion propre lors de la destruction du contrôleur
  disconnect() {
    if (this.dialogTarget.open) {
      this.dialogTarget.close()
    }
  }
}